/**
 * This component is deprecated as part of a larger effort to deprecate Chart.js from our code base, and no
 * new development involving Chart.js should be done. Instead favor using visx with the components in
 * /ui-library/charting/.
 */
import Chart from 'chart.js';
import React, {useCallback, useEffect, useRef} from 'react';

import {isNodeTesting} from 'lib/Browser';
import {FONT_FAMILY_PROXIMA, LEGACY_FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

Chart.defaults.global.defaultFontFamily = FONT_FAMILY_PROXIMA;
Chart.defaults.global.defaultFontSize = LEGACY_FONT_PROXIMA_13.fontSize;

function setUpChart(canvasEl: HTMLCanvasElement, chartConfiguration: Chart.ChartConfiguration): Chart {
  return new Chart(canvasEl, chartConfiguration);
}

function updateChartIfExists(chart: Chart | null, chartConfiguration: Chart.ChartConfiguration): void {
  if (chart) {
    // Bit surprising, but according to https://www.chartjs.org/docs/latest/developers/updates.html, mutating
    // data and options in place is supported.
    Object.assign(chart, chartConfiguration);
    chart.update();
  }
}

function tearDownChartIfExists(chart: Chart | null): void {
  if (chart) {
    chart.destroy();
  }
}

/*
 * Chart.js chart
 *
 * Don't use this component directly, so that we're not tightly coupled to Chart.js. Use wrapper components
 * like DeprecatedBarChart instead.
 */
export default function DeprecatedChartJSChart({
  chartConfiguration,
  height,
  dataTestAttrs,
}: {
  chartConfiguration: Chart.ChartConfiguration;
  // Pass a value to get a fixed-height, variable-width chart
  // (https://github.com/chartjs/Chart.js/issues/3384#issuecomment-387896476)
  height?: string | number;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const chartRef = useRef<Chart | null>(null);

  // Re-render chart if (1) chartConfiguration or (2) <canvas> node changes
  //
  // (1) Update chart if chartConfiguration changes
  useEffect(() => {
    // Chart.JS can't render when running in our unit tests because there is no Canvas element for it to point to,
    // so we cancel the actual rendering/update logic while DeprecatedChartJSChart is running in unit tests.
    if (isNodeTesting) {
      return;
    }
    updateChartIfExists(chartRef.current, chartConfiguration);
  }, [chartConfiguration]);

  // (2) Tear down and set up chart if node changes:
  //     (a) If node changes from null to defined (aka mount), set up chart
  //     (b) If node changes from defined to defined, tear down chart at old node and set up chart at new node
  //     (c) If node changes from defined to null (aka unmount), tear down chart at old node
  const canvasRef = useCallback(
    (canvasEl: HTMLCanvasElement) => {
      if (isNodeTesting) {
        return;
      }
      tearDownChartIfExists(chartRef.current);
      chartRef.current = canvasEl ? setUpChart(canvasEl, chartConfiguration) : null;
    },
    [chartConfiguration]
  );

  return (
    <div
      className="u-flex-grow u-no-select u-overflow-hidden u-position-relative u-width-full u-height-full"
      {...getTestProps(dataTestAttrs)}
    >
      <canvas ref={canvasRef} />
    </div>
  );
}
